import { defineStore } from "pinia";
import { OfficesApi } from "../api";
import { MapType } from "../model/mapModels";
import L from "leaflet";

export const useMapStore = defineStore({
  id: "map",
  state: () => ({
    mapObject: null as L.Map | null,
    additionalLayerGroups: {} as Record<string, L.LayerGroup>,
    legend: null as L.Control | null,
    chosenMapType: null as MapType | null,
    isMapReady: false,
    geopackageLayers: [] as Array<{
      id: string;
      name: string;
      visible: boolean;
      style: any;
      layerId?: number;
      geometryType: string;
    }>,
  }),
  getters: {
    getCurrentLocation: (state) => {
      return state.currentLocation;
    },
    getLayerGroups: (state) => {
      return state.additionalLayerGroups;
    },
    getChosenMapType: (state) => {
      return state.chosenMapType;
    },
    // getMapObject: (state) => {
    //   return state.mapObject;
    // },
    getLegend: (state) => {
      return state.legend;
    },
  },
  actions: {
    setMapObject(map: L.Map) {
      this.mapObject = map;
      this.isMapReady = true;
    },
    async waitForMap(): Promise<boolean> {
      const start = Date.now();
      let attempts = 0;
      const maxAttempts = 10;

      while (!this.isMapReady && attempts < maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, 100));
        attempts++;
      }
      return this.isMapReady;
    },
    async fitMapToBounds(bounds: L.LatLngBounds) {
      if (!bounds || !bounds.isValid()) {
        console.warn("Invalid bounds provided");
        return;
      }

      const isReady = await this.waitForMap();
      if (!isReady || !this.mapObject) {
        console.warn("Map not ready for bounds fitting");
        return;
      }

      try {
        // Force a reflow of the map container
        this.mapObject.invalidateSize(true);

        // Wait for container to be ready
        await new Promise((resolve) => setTimeout(resolve, 200));

        // Check if map is still valid
        if (!this.mapObject || !this.mapObject.getContainer()) {
          console.warn("Map container lost during bounds fitting");
          return;
        }

        // Fit bounds with conservative options
        this.mapObject.fitBounds(bounds, {
          padding: [30, 30],
          maxZoom: 10,
          animate: false,
          duration: 0,
        });
      } catch (error) {
        console.error("Error in fitMapToBounds:", error);
      }
    },

    async addLayerGroup(
      id: string,
      layerGroup: L.LayerGroup | L.GeoJSON,
      displayName?: string,
      style?: any,
    ) {
      const isReady = await this.waitForMap();
      if (!isReady || !this.mapObject) {
        console.warn("Map not ready for adding layer");
        return false;
      }

      try {
        // Remove existing layer if present
        this.removeLayerGroup(id);

        // Wait for removal to complete
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Set display name on the layer for controls/legends if provided
        if (displayName) {
          layerGroup.options = layerGroup.options || {};
          layerGroup.options.name = displayName;
        }

        // Add new layer
        layerGroup.addTo(this.mapObject);
        this.additionalLayerGroups[id] = layerGroup;

        // Save the display name in a property for later use
        if (displayName) {
          // If we have a dedicated property for display names, use it
          // Otherwise add it as a custom property
          this.layerDisplayNames = this.layerDisplayNames || {};
          this.layerDisplayNames[id] = displayName;
        }

        // If this is a GeoPackage layer, track it
        if (id.startsWith("geopackage-")) {
          // Check if we already have this layer
          const existingIndex = this.geopackageLayers.findIndex(
            (l) => l.id === id,
          );

          if (existingIndex >= 0) {
            // Update existing layer
            this.geopackageLayers[existingIndex].visible = true;
            if (style) {
              this.geopackageLayers[existingIndex].style = style;
            }
          } else {
            // Add new layer
            this.geopackageLayers.push({
              id,
              name: displayName || id.replace("geopackage-", ""),
              visible: true,
              style: style || {},
              // Try to detect geometry type from the layer
              geometryType: this.detectGeometryType(layerGroup),
            });
          }
        }

        // Calculate bounds from all layers in the group
        const bounds = this.calculateLayerGroupBounds(layerGroup);
        if (bounds && bounds.isValid()) {
          await this.fitMapToBounds(bounds);
        }

        return true;
      } catch (error) {
        console.error(
          `Error adding layer group "${displayName || id}":`,
          error,
        );
        return false;
      }
    },

    calculateLayerGroupBounds(layerGroup: L.LayerGroup): L.LatLngBounds | null {
      try {
        let bounds: L.LatLngBounds | null = null;

        layerGroup.eachLayer((layer: any) => {
          // Get bounds based on layer type
          let layerBounds: L.LatLngBounds | undefined;

          if (layer.getBounds) {
            // For circles, polygons, etc.
            layerBounds = layer.getBounds();
          } else if (layer.getLatLng) {
            // For markers, etc.
            const latLng = layer.getLatLng();
            layerBounds = L.latLngBounds([latLng, latLng]);
          }

          if (layerBounds && layerBounds.isValid()) {
            if (bounds === null) {
              bounds = layerBounds;
            } else {
              bounds.extend(layerBounds);
            }
          }
        });

        return bounds;
      } catch (error) {
        console.error("Error calculating layer group bounds:", error);
        return null;
      }
    },
    removeLayerGroup(name: string) {
      if (!this.mapObject) return;

      try {
        const layer = this.additionalLayerGroups[name];
        if (layer) {
          this.mapObject.removeLayer(layer);
          delete this.additionalLayerGroups[name];

          // Also remove from geopackageLayers array
          const geoLayerIndex = this.geopackageLayers.findIndex(
            (l) => l.id === name,
          );
          if (geoLayerIndex !== -1) {
            this.geopackageLayers.splice(geoLayerIndex, 1);
          }

          // Clean up layer display name
          delete this.layerDisplayNames[name];
        }
      } catch (error) {
        console.error("Error removing layer:", error);
      }
    },
    removeLegend() {
      if (this.legend && this.mapObject) {
        this.legend.remove();
        this.legend = null;
      }
    },
    addLegend(legend: L.Control) {
      if (this.mapObject) {
        this.legend = legend;
        legend.addTo(this.mapObject);
      }
    },
    setChosenMapType(mapType: MapType) {
      this.chosenMapType = mapType;
    },
    async fetchCurrentLocation() {
      new OfficesApi();
    },
    addControl(control: L.Control) {
      if (this.mapObject) {
        control.addTo(this.mapObject);
      }
    },
    removeControl(control: L.Control) {
      if (this.mapObject) {
        control.remove();
      }
    },
    /**
     * Update the style of a GeoPackage layer
     */
    async updateGeoPackageLayerStyle(layerId: string, style: any) {
      // Find the layer in geopackageLayers
      const layer = this.geopackageLayers.find((l) => l.id === layerId);
      if (!layer) return false;

      // Update the style in local state
      layer.style = style;

      // Get the layer instance
      const layerInstance = this.getLayerGroups[layerId];
      if (!layerInstance) return false;

      try {
        // Extract actual GeoJSON layer - this assumes your layer groups may contain GeoJSON layers
        if (layerInstance instanceof L.LayerGroup) {
          // Apply style to all sublayers
          layerInstance.eachLayer((sublayer) => {
            if (sublayer instanceof L.GeoJSON) {
              // First, update the style option
              sublayer.options.style = () => style;

              // Then, apply the style to all existing paths
              sublayer.eachLayer((feature) => {
                if (feature instanceof L.Path) {
                  feature.setStyle(style);
                } else if (feature instanceof L.Marker) {
                  // Handle markers if needed
                  // You might need to recreate the marker with a new icon
                }
              });
            }
          });
        } else if (layerInstance instanceof L.GeoJSON) {
          // Direct GeoJSON layer
          layerInstance.setStyle(style);
        }

        // Save to backend if needed
        if (typeof layer.layerId === "number") {
          try {
            // Assuming your API supports updating layer styles
            const response = await fetch(
              `/api/geopackage/layers/${layer.layerId}/style`,
              {
                method: "PUT",
                headers: {
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({ style }),
              },
            );

            if (!response.ok) {
              console.error("Failed to save style to server");
            }
          } catch (error) {
            console.error("Error saving style:", error);
          }
        }

        return true;
      } catch (error) {
        console.error("Error updating layer style:", error);
        return false;
      }
    },
    detectGeometryType(layer: L.LayerGroup | L.GeoJSON): string {
      try {
        let geometryType = "Unknown";

        // Check for GeoJSON layer
        if (layer instanceof L.GeoJSON) {
          // Look at the features
          layer.eachLayer((feature) => {
            if (feature instanceof L.Marker) {
              geometryType = "Point";
              return false; // break the loop
            } else if (
              feature instanceof L.Polyline &&
              !(feature instanceof L.Polygon)
            ) {
              geometryType = "LineString";
              return false;
            } else if (feature instanceof L.Polygon) {
              geometryType = "Polygon";
              return false;
            }
          });
        } else if (layer instanceof L.LayerGroup) {
          // Check sublayers
          layer.eachLayer((sublayer) => {
            if (sublayer instanceof L.GeoJSON) {
              // Recurse
              const subType = this.detectGeometryType(sublayer);
              if (subType !== "Unknown") {
                geometryType = subType;
                return false;
              }
            } else if (sublayer instanceof L.Marker) {
              geometryType = "Point";
              return false;
            } else if (
              sublayer instanceof L.Polyline &&
              !(sublayer instanceof L.Polygon)
            ) {
              geometryType = "LineString";
              return false;
            } else if (sublayer instanceof L.Polygon) {
              geometryType = "Polygon";
              return false;
            }
          });
        }

        return geometryType;
      } catch (error) {
        console.error("Error detecting geometry type:", error);
        return "Unknown";
      }
    },
  },
});
